export interface PromptsOptions {
    callback?: (rememberChoice: boolean) => void;
    headerText?: string;
    bodyText?: string;
    yesButtonText?: string;
    noButtonText?: string;
}

export interface RememberChoiceOptions {
    enabled?: boolean | string;
    promptFn?: (callback: (rememberChoice: boolean) => void) => void;
    prompt?: PromptsOptions;
}

export interface AppSelectionOptions {
    dialogHeaderText?: string;
    cancelButtonText?: string;
    list?: string[];
    callback?: (app: string) => void;
    androidTheme?: number;
    rememberChoice?: RememberChoiceOptions;
}

export interface LaunchNavigatorOptions {
    successCallback?: () => void;
    errorCallback?: (error: string) => void;
    app?: string;
    destinationName?: string;
    start?: string | number[];
    startName?: string;
    transportMode?: string;
    enableDebug?: boolean;
    extras?: Record<string, unknown>;
    launchModeGoogleMaps?: string;
    enableGeolocation?: boolean;
    enableGeocoding?: boolean;
    appSelection?: AppSelectionOptions;
    plz?: string;
    ort?: string;
    lkz?: string;
    strasse?: string;
    hausnummer?: string;
    latitude?: number;
    longitude?: number;
    geoKoordinatenBevorzugen?: string;
}

export interface NavigateNativeOptions {
    app: string;
    dType: string;
    dest: string;
    destNickname?: string;
    sType: string;
    start?: string;
    startNickname?: string;
    transportMode?: string;
    launchMode?: string;
    extras?: string;
    enableGeocoding?: boolean;
    plz?: string;
    ort?: string;
    lkz?: string;
    strasse?: string;
    hausnummer?: string;
    latitude?: number;
    longitude?: number;
    geoKoordinatenBevorzugen?: string;
}

export const LAUNCH_NAVIGATOR_APP = {
    USER_SELECT: 'user_select',
    APPLE_MAPS: 'apple_maps',
    GOOGLE_MAPS: 'google_maps',
    WAZE: 'waze',
    CITYMAPPER: 'citymapper',
    NAVIGON: 'navigon',
    TRANSIT_APP: 'transit_app',
    YANDEX: 'yandex',
    UBER: 'uber',
    TOMTOM: 'tomtom',
    BING_MAPS: 'bing_maps',
    SYGIC: 'sygic',
    HERE_MAPS: 'here_maps',
    MOOVIT: 'moovit',
    LYFT: 'lyft',
    MAPS_ME: 'maps_me',
    CABIFY: 'cabify',
    BAIDU: 'baidu',
    TAXIS_99: 'taxis_99',
    GAODE: 'gaode',
    GEO: 'geo',
} as const;

export const LAUNCH_NAVIGATOR_LAUNCH_MODE = {
    MAPS: 'maps',
    TURN_BY_TURN: 'turn-by-turn',
    GEO: 'geo',
} as const;

export const LAUNCH_NAVIGATOR_TRANSPORT_MODE = {
    DRIVING: 'driving',
    WALKING: 'walking',
    BICYCLING: 'bicycling',
    TRANSIT: 'transit',
} as const;

export const LAUNCH_NAVIGATOR_APP_NAMES: Record<string, string> = {
    [LAUNCH_NAVIGATOR_APP.USER_SELECT]: '[User select]',
    [LAUNCH_NAVIGATOR_APP.APPLE_MAPS]: 'Apple Maps',
    [LAUNCH_NAVIGATOR_APP.GOOGLE_MAPS]: 'Google Maps',
    [LAUNCH_NAVIGATOR_APP.WAZE]: 'Waze',
    [LAUNCH_NAVIGATOR_APP.CITYMAPPER]: 'Citymapper',
    [LAUNCH_NAVIGATOR_APP.NAVIGON]: 'Navigon',
    [LAUNCH_NAVIGATOR_APP.TRANSIT_APP]: 'Transit App',
    [LAUNCH_NAVIGATOR_APP.YANDEX]: 'Yandex Navigator',
    [LAUNCH_NAVIGATOR_APP.UBER]: 'Uber',
    [LAUNCH_NAVIGATOR_APP.TOMTOM]: 'Tomtom',
    [LAUNCH_NAVIGATOR_APP.BING_MAPS]: 'Bing Maps',
    [LAUNCH_NAVIGATOR_APP.SYGIC]: 'Sygic',
    [LAUNCH_NAVIGATOR_APP.HERE_MAPS]: 'HERE Maps',
    [LAUNCH_NAVIGATOR_APP.MOOVIT]: 'Moovit',
    [LAUNCH_NAVIGATOR_APP.LYFT]: 'Lyft',
    [LAUNCH_NAVIGATOR_APP.MAPS_ME]: 'MAPS.ME',
    [LAUNCH_NAVIGATOR_APP.CABIFY]: 'Cabify',
    [LAUNCH_NAVIGATOR_APP.BAIDU]: 'Baidu Maps',
    [LAUNCH_NAVIGATOR_APP.TAXIS_99]: '99 Taxi',
    [LAUNCH_NAVIGATOR_APP.GAODE]: 'Gaode Maps (Amap)',
    [LAUNCH_NAVIGATOR_APP.GEO]: '[Geo intent chooser]',
};

export const LAUNCH_NAVIGATOR_ANDROID_APPS: string[] = [
    LAUNCH_NAVIGATOR_APP.USER_SELECT,
    LAUNCH_NAVIGATOR_APP.GEO,
    LAUNCH_NAVIGATOR_APP.GOOGLE_MAPS,
    LAUNCH_NAVIGATOR_APP.CITYMAPPER,
    LAUNCH_NAVIGATOR_APP.UBER,
    LAUNCH_NAVIGATOR_APP.WAZE,
    LAUNCH_NAVIGATOR_APP.YANDEX,
    LAUNCH_NAVIGATOR_APP.SYGIC,
    LAUNCH_NAVIGATOR_APP.HERE_MAPS,
    LAUNCH_NAVIGATOR_APP.MOOVIT,
    LAUNCH_NAVIGATOR_APP.LYFT,
    LAUNCH_NAVIGATOR_APP.MAPS_ME,
    LAUNCH_NAVIGATOR_APP.CABIFY,
    LAUNCH_NAVIGATOR_APP.BAIDU,
    LAUNCH_NAVIGATOR_APP.TAXIS_99,
    LAUNCH_NAVIGATOR_APP.GAODE,
];

const COORDS_REGEX = /^[-\d.]+,[\s]*[-\d.]+$/;

export function getAppDisplayName(app: string): string {
    return LAUNCH_NAVIGATOR_APP_NAMES[app] || app;
}

export function extractCoordsFromLocationString(location: string | number[]): string | number[] {
    if (location && typeof location === 'string' && location.match(COORDS_REGEX)) {
        const normalized = location.replace(/\s*/g, '');
        const parts = normalized.split(',');
        return [Number(parts[0]), Number(parts[1])];
    }
    return location;
}

export function isValidApp(app: string): boolean {
    if (app === 'none') {
        return true;
    }
    if (Object.values(LAUNCH_NAVIGATOR_APP).includes(app as typeof LAUNCH_NAVIGATOR_APP[keyof typeof LAUNCH_NAVIGATOR_APP])) {
        return true;
    }
    return app.includes('.');
}

export function isValidTransportMode(transportMode: string): boolean {
    return Object.values(LAUNCH_NAVIGATOR_TRANSPORT_MODE).includes(
        transportMode as typeof LAUNCH_NAVIGATOR_TRANSPORT_MODE[keyof typeof LAUNCH_NAVIGATOR_TRANSPORT_MODE]
    );
}

export function isValidLaunchMode(launchMode: string): boolean {
    return Object.values(LAUNCH_NAVIGATOR_LAUNCH_MODE).includes(
        launchMode as typeof LAUNCH_NAVIGATOR_LAUNCH_MODE[keyof typeof LAUNCH_NAVIGATOR_LAUNCH_MODE]
    );
}
